/**
 * Module identity.
 *
 * The operations modules — Policies, Reports, Meetings, Tasks, ToDo — share one
 * design system, but a user who lands mid-workflow should be able to tell which
 * one they are in before reading the title. Each module therefore owns a small
 * accent that appears in exactly four places: the rule above the page title, the
 * section icon tile, the filter panel's heading icon, and the table header tint.
 *
 * It never becomes a page fill. The 75/25 neutral-to-colour split the brand
 * calls for only survives if the accent stays in those four small surfaces.
 *
 * Every value resolves against the MUI palette rather than a literal hex, so an
 * installation that reconfigures its theme from Setting → Theme Color still
 * drives these. `indigo` and `teal` have no palette slot of their own, so they
 * borrow `secondary` and `success` — the two families closest to them in the
 * configured scheme — instead of hard-coding a colour that would ignore the
 * user's theme.
 */
import type { Theme } from "@mui/material/styles";
import { GT } from "../../theme/appcolors";

export type ModuleKey =
  | "dashboard"
  | "policies"
  | "reports"
  | "policyReport"
  | "meetings"
  | "tasks"
  | "todo"
  | "clients"
  | "staff"
  | "newsletter"
  | "workshop"
  | "settings"
  | "documentation";

type PaletteFamily = "primary" | "secondary" | "info" | "success" | "warning" | "error";

/**
 * Which palette family carries each module, and which carries its secondary
 * highlight (used for the second stop of the title rule's gradient).
 */
const FAMILY: Record<ModuleKey, { main: PaletteFamily; pair: PaletteFamily }> = {
  /* blue -> cyan */
  dashboard: { main: "primary", pair: "info" },
  meetings: { main: "info", pair: "primary" },
  /* cyan -> teal: Clients is the most-visited list and reads as its own place */
  clients: { main: "info", pair: "success" },
  /* blue -> indigo */
  policies: { main: "primary", pair: "secondary" },
  /* indigo -> blue */
  reports: { main: "secondary", pair: "primary" },
  policyReport: { main: "secondary", pair: "primary" },
  /* indigo -> amber */
  tasks: { main: "secondary", pair: "warning" },
  /* teal -> emerald */
  todo: { main: "success", pair: "info" },
  /* violet -> cyan */
  staff: { main: "secondary", pair: "info" },
  /* violet -> rose */
  newsletter: { main: "secondary", pair: "error" },
  /* amber -> indigo */
  workshop: { main: "warning", pair: "secondary" },
  /* slate -> indigo */
  settings: { main: "secondary", pair: "primary" },
  documentation: { main: "info", pair: "secondary" },
};

export interface ModuleAccent {
  /** The module's dominant accent. */
  main: string;
  /** Its companion, for the two-stop rule under the breadcrumb. */
  pair: string;
  /** Text-safe variant of `main` for the current mode. */
  ink: string;
}

/** `#AABBCC` / `#abc` / `rgb(…)` reduced to a comparable key. */
const normalise = (value: string): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/, "#$1$1$2$2$3$3");

/** HSL saturation, 0–1. Returns 1 for anything unparseable so it is left alone. */
const saturationOf = (value: string): number => {
  const hex = normalise(value);
  const match = /^#([0-9a-f]{6})$/.exec(hex);
  if (!match) {
    const rgb = /rgba?\((\d+),(\d+),(\d+)/.exec(hex);
    if (!rgb) return 1;
    return saturationOfRgb(+rgb[1], +rgb[2], +rgb[3]);
  }
  const n = parseInt(match[1], 16);
  /* eslint-disable no-bitwise */
  return saturationOfRgb((n >> 16) & 255, (n >> 8) & 255, n & 255);
  /* eslint-enable no-bitwise */
};

const saturationOfRgb = (r: number, g: number, b: number): number => {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const delta = max - min;
  if (delta === 0) return 0;
  const lightness = (max + min) / 2;
  return delta / (1 - Math.abs(2 * lightness - 1));
};

/**
 * The built-in premium accents, used only when the configured theme has none of
 * its own.
 *
 * Setting → Theme Color lets an installation pick `primary` and `secondary`, and
 * this one has set both to the same brokerage blue (`#0079b9`). Every module
 * whose identity resolves through `secondary` — Reports, Tasks, Staff,
 * Newsletter, Settings — therefore collapses onto the primary blue, and the
 * whole product reads as one undifferentiated blue-and-grey admin tool. That is
 * the "everything looks the same and nothing looks designed" complaint, and it
 * is a *theme configuration* symptom rather than a design-system one.
 *
 * So: the configured colour still wins whenever it is genuinely distinct. It is
 * only when `secondary` is indistinguishable from `primary` that the module
 * falls back to the accent the design system was drawn against. A client who
 * later sets a real secondary colour immediately takes back control of it.
 */
const FALLBACK: Partial<Record<PaletteFamily, { light: string; dark: string }>> = {
  secondary: { light: GT.indigo, dark: GT.indigoLight },
};

const familyColour = (
  theme: Theme,
  family: PaletteFamily,
  variant: "main" | "ink"
): string => {
  const palette = theme.palette[family];
  const isLight = theme.palette.mode === "light";
  const fallback = FALLBACK[family];

  /* The configured colour is kept whenever it can actually carry an identity.
     It is replaced in exactly two cases, both of which mean "this theme has no
     secondary of its own":

       - it is the same colour as primary (this installation's light theme sets
         both to #0079b9), or
       - it is so close to grey that it reads as chrome rather than as an accent
         (its dark theme sets #8c9cb7, a blue-grey at ~23% saturation).

     Below that threshold a module tint is indistinguishable from a border, which
     is precisely the "everything is grey and nothing looks designed" reading. */
  const collapsed =
    family !== "primary" &&
    !!fallback &&
    (normalise(palette.main) === normalise(theme.palette.primary.main) ||
      saturationOf(palette.main) < 0.32);

  if (collapsed && fallback) {
    return isLight
      ? variant === "ink"
        ? GT.indigoDeep
        : fallback.light
      : variant === "ink"
      ? fallback.dark
      : fallback.dark;
  }

  if (variant === "main") return palette.main;
  /* Dark surfaces need the lighter end of the family and light surfaces the
     darker end, or an amber-family accent renders as unreadable yellow text
     on white. */
  return (isLight ? palette.dark : palette.light) || palette.main;
};

/**
 * The same resolution for a component that names a palette family directly
 * rather than a module — a "filter" button asking for indigo, say. Keeps those
 * controls in step with the module accents instead of collapsing onto primary
 * on a theme that configured no distinct secondary.
 */
export const paletteAccent = (
  theme: Theme,
  family: PaletteFamily
): { main: string; ink: string } => ({
  main: familyColour(theme, family, "main"),
  ink: familyColour(theme, family, "ink"),
});

export const moduleAccent = (theme: Theme, key: ModuleKey): ModuleAccent => {
  const spec = FAMILY[key] ?? FAMILY.policies;

  return {
    main: familyColour(theme, spec.main, "main"),
    pair: familyColour(theme, spec.pair, "main"),
    ink: familyColour(theme, spec.main, "ink"),
  };
};
